import React, { useEffect, useState } from "react";
import {
  Button,
  Collapse,
  Form,
  InputNumber,
  Popover,
  Spin,
  Tag,
  Tooltip,
} from "antd";
import {
  DeleteOutlined,
  EditOutlined,
  HistoryOutlined,
  InfoCircleOutlined,
  LoadingOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import AddKpiModal from "../modal/add/AddKpiModal";
import GetKpisApi from "../../api/kpis/GetKpisApi";
import { useLocation } from "react-router-dom";
import PageLoader from "../sub/PageLoader";
import { getTargetString } from "../../helpers/targetHelpers";
import {
  openNotificationError,
  openNotificationSuccess,
} from "../../helpers/NotificationHelpers";
import DeleteKpiApi from "../../api/kpis/DeleteKpiApi";
import ConfirmationModal from "../modal/ConfirmationModal";
import EditKpiModal from "../modal/edit/EditKpiModal";
import GetKpiProgressHistoryApi from "../../api/kpis/GetKpiProgressHistoryApi";
import KpiProgressHistoryTable from "../tables/KpiProgressHistoryTable";
import { useSelector } from "react-redux";
import UpdateKpiProgressApi from "../../api/kpi-progress/UpdateKpiProgressApi";

const { Panel } = Collapse;

function TopPlanKpi(props) {
  const [canStart, setCanStart] = useState(false);
  const [canShow, setCanShow] = useState(false);
  const [data, setData] = useState();
  const [addModal, setAddModal] = useState(false);
  const positionState = useSelector((state) => state.positionState.value);
  const proHeight = document.body.clientHeight;
  let curLocation = useLocation();

  useEffect(() => {
    getKpi();
  }, [curLocation]);

  async function getKpi() {
    let path = curLocation.pathname;
    let id = path.split("/top-plans/");
    const apiResponse = await GetKpisApi(
      props.data?.planId ? props.data?.planId : id[1],
      positionState?.id
    );
    if (apiResponse.message === "success") {
      setData(apiResponse);
      if (apiResponse.kpis?.length) {
        setCanShow(true);
      }
    }
    setCanStart(true);
  }

  function showEmptyKpi() {
    return (
      <div className="flex justify-center items-center w-full h-full py-8">
        <div className="text-center">
          <div className="text-3xl text-gray-400">
            <WarningOutlined />
          </div>
          <div className="text-xl pri-font text-gray-400">
            No KPIs available
          </div>
          <div className="mb-3 text-gray-400">Add KPIs to this plan</div>
          <Button onClick={() => setAddModal(true)} type="primary">
            Add a KPI
          </Button>
        </div>
      </div>
    );
  }

  function getPerformance(kpi) {
    let output = 0;
    output = ((kpi.targetAchieved / kpi.target) * 100).toFixed(2);
    return output;
  }

  function renderKpi() {
    let output = [];

    data?.kpis?.forEach((kpi) => {
      output.push(
        <Panel
          header={
            <div className="flex justify-between w-full">
              <div>
                {kpi.name +
                  " - " +
                  getTargetString(
                    kpi.target,
                    kpi.scale?.name,
                    kpi.scaleunit?.abbreviation
                  )}
              </div>
              <div>
                <Tag color="Red">{getPerformance(kpi) + "%"}</Tag>
              </div>
            </div>
          }
          key={kpi.id}
        >
          <KpiPanels kpi={kpi} getKpi={() => getKpi()} />
        </Panel>
      );
    });

    return output;
  }

  return (
    <>
      {canStart ? (
        canShow ? (
          <div className="mb-5 max-w-4xl">
            <Collapse bordered={false}>
              {renderKpi()}
              <div className="py-3 px-4">
                <div
                  onClick={() => setAddModal(true)}
                  className="cursor-pointer text-blue-500"
                >
                  + Add new KPI
                </div>
              </div>
            </Collapse>
          </div>
        ) : (
          showEmptyKpi()
        )
      ) : (
        <div
          style={{ height: proHeight - 188 }}
          className="flex justify-center items-center w-full h-full"
        >
          <PageLoader />
        </div>
      )}
      <AddKpiModal
        visible={addModal}
        handleCancel={() => setAddModal(false)}
        handleOk={() => getKpi()}
        planId={props.data?.planId ? props.data?.planId : ""}
      />
    </>
  );
}

export default TopPlanKpi;

export function KpiPanels(props) {
  const [submit, setSubmit] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState();
  const [historyLoading, setHistoryLoading] = useState(false);
  const [update, setUpdate] = useState(false);
  const positionState = useSelector((state) => state.positionState.value);

  async function deleteKpi() {
    const apiResponse = await DeleteKpiApi(props.kpi.id);
    if (apiResponse.statusCode) {
      openNotificationError("bottomRight", apiResponse.message);
    } else if (apiResponse.message === "success") {
      openNotificationSuccess("bottomRight", "Kpi deleted");
    } else {
      openNotificationError("bottomRight", "Couldn't delete. Try again later.");
    }
  }

  async function updateTarget(values) {
    setSubmit(true);
    const apiResponse = await UpdateKpiProgressApi(
      props.kpi.id,
      values.target,
      positionState?.id
    );
    if (apiResponse.statusCode) {
      openNotificationError("bottomRight", apiResponse.message);
    } else if (apiResponse.message === "success") {
      openNotificationSuccess("bottomRight", `Target updated`);
      props.getKpi();
    } else {
      openNotificationError(
        "bottomRight",
        "Couldn't update kpi. Try again later."
      );
    }
    setSubmit(false);
  }

  function expandHistory() {
    setShowHistory(!showHistory);
    if (!history) {
      getProgressHistory();
    }
  }

  async function getProgressHistory() {
    setHistoryLoading(true);
    const apiResponse = await GetKpiProgressHistoryApi(props.kpi.id);
    if (apiResponse.success) {
      setHistory(apiResponse.data);
      setHistoryLoading(false);
    }
    setHistoryLoading(false);
  }

  return (
    <>
      <>
        <div className="px-6">
          <div className="mb-6 max-w-3xl">
            <div className="border rounded">
              <div
                className={
                  "p-3 flex justify-between items-center " +
                  (showHistory && "border-b")
                }
              >
                <div className="flex items-center">
                  <div>
                    <div className="">Target</div>
                    <Form
                      name="updateProgress"
                      onFinish={(values) => updateTarget(values)}
                      autoComplete="off"
                      layout="vertical"
                      requiredMark={false}
                      initialValues={{
                        target: props.kpi?.targetAchieved,
                      }}
                    >
                      <div className="flex items-center">
                        {update ? (
                          <Form.Item name="target" style={{ marginBottom: 0 }}>
                            <InputNumber />
                          </Form.Item>
                        ) : (
                          <div className="text-lg">
                            {props.kpi?.targetAchieved
                              ? getTargetString(
                                  props.kpi?.targetAchieved,
                                  props.kpi.scale?.name,
                                  props.kpi.scaleunit?.abbreviation
                                )
                              : "0"}
                          </div>
                        )}
                        <div className="mx-1 text-lg">/</div>
                        <div className="text-lg">
                          {getTargetString(
                            props.kpi?.target,
                            props.kpi?.scale?.name,
                            props.kpi?.scaleunit?.abbreviation
                          )}
                        </div>

                        {update && (
                          <Form.Item name="target" style={{ marginBottom: 0 }}>
                            <div className="ml-5 flex">
                              <Button
                                className="w-20"
                                htmlType={submit && "submit"}
                              >
                                {submit ? (
                                  <Spin
                                    size="small"
                                    indicator={<LoadingOutlined spin />}
                                  />
                                ) : (
                                  "Update"
                                )}
                              </Button>
                              <Button
                                onClick={() => setUpdate(false)}
                                type={"text"}
                              >
                                cancel
                              </Button>
                            </div>
                          </Form.Item>
                        )}
                      </div>
                    </Form>
                  </div>
                </div>
                <div className="flex items-center">
                  {props.kpi?.canUpdate ? (
                    !update && (
                      <Button onClick={() => setUpdate(true)} type="primary">
                        Update
                      </Button>
                    )
                  ) : (
                    <>
                      <Popover
                        placement="top"
                        content={
                          <div className="text-xs">
                            You can't update this target
                          </div>
                        }
                        trigger="hover"
                      >
                        <div className="text-gray-500 text-xs mr-1">
                          <InfoCircleOutlined />
                        </div>
                      </Popover>
                      <Button disabled type="primary">
                        Update
                      </Button>
                    </>
                  )}
                  <Tooltip title={"Update History"}>
                    <div
                      className="cursor-pointer text-blue-500 hover:text-blue-600 ml-2"
                      onClick={() => expandHistory()}
                    >
                      <HistoryOutlined />
                    </div>
                  </Tooltip>
                </div>
              </div>
              {showHistory && (
                <div className="max-h-64 overflow-auto custom-scroll">
                  <div className="p-3">
                    <KpiProgressHistoryTable
                      data={history}
                      handleOk={() => getProgressHistory()}
                      loading={historyLoading}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className="mt-6">
            <Button
              className="mr-2"
              danger
              onClick={() => setDeleteModal(true)}
            >
              <DeleteOutlined />
            </Button>
            <Button onClick={() => setEditModal(true)}>
              <EditOutlined />
            </Button>
          </div>
        </div>
      </>
      <EditKpiModal
        visible={editModal}
        handleCancel={() => setEditModal(false)}
        editKpi={props.kpi}
        handleOk={() => props.getKpi()}
      />
      <ConfirmationModal
        visible={deleteModal}
        handleCancel={() => setDeleteModal(false)}
        handleOk={() => deleteKpi()}
        text="Are you sure want to delete this Kpi?"
        title="Delete KPI"
      />
    </>
  );
}
